import React from 'react';
import { connect } from 'react-redux';
import { Button, Card, CardMedia, CardActions, Typography, CardActionArea, Badge } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import ConfirmationDialog from '../../../dialogs/ConfirmationDialog';
import PlayIcon from '@mui/icons-material/PlayArrowRounded';
import VideoViewerDialog from '../../../dialogs/VideoViewerDialog';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';
import AppConstants from '../../../../App/AppConstants';
import { createActions } from '../store';
import { mediaPickerActions } from '../MediaPickers/store';

const cardWidth = 260;

const styles = theme => ({
    card: {
        width: cardWidth
    },
    media: {
        height: 0,
        paddingTop: '56.25%', // 16:9
        width: '100%',
    },
    mediaButton: {
        width: '100%',
        '&:hover': {
            '& svg': {
                opacity: 1
            }
        }
    },
    playIcon: {
        position: 'absolute',
        fontSize: '3.5rem',
        background: theme.palette.text.secondary,
        color: '#000000',
        padding: theme.spacing(1),
        borderRadius: '999px',
        opacity: 0,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        transition: theme.transitions.create('opacity', {
			easing: theme.transitions.easing.easeInOut,
			duration: theme.transitions.duration.shorter,
		})
    },
    proLabel: {
        opacity: 0.8,
        position: 'absolute',
        width: 240,
        textAlign: 'end',
        paddingTop: 8,
        fontWeight: 'bold',
        fontSize: '15pt'
    }
});

const PresetCardBadge = withStyles(Badge, {
    badge: {
        fontSize: '0.80rem',
        padding: '12px 6px',
        right: 6,
        pointerEvents: 'none'
    }
});

class PresetCard extends React.Component { 
    state = {
        raised: false,
        confirmationOpen: false,
        viewerOpen: false
    }

    render() {
        const { classes, preset } = this.props;
        const { raised, confirmationOpen, viewerOpen } = this.state;

        return (
            <PresetCardBadge 
                badgeContent={'NEW'} 
                color='primary'
                invisible={!preset.recentlyAdded}
            >
                <Card
                    className={classes.card}
                    raised={raised}
                    onMouseEnter={() => this.setState({ raised: true })}
                    onMouseLeave={() => this.setState({ raised: false })}
                >
                    <CardActionArea 
                        className={classes.mediaButton}
                        onClick={this.onCardAreaClick}
                        aria-label='View example video'
                    >
                        {preset.isPremium &&
                            <Typography className={classes.proLabel}>
                                Pro
                            </Typography>
                        }
                        <CardMedia
                            className={classes.media}
                            image={preset.thumbnailUrl}
                            alt='Preset thumbnail'
                        />
                        <PlayIcon className={classes.playIcon}/>
                    </CardActionArea>
                    <CardActions>
                        <Typography>{preset.name}</Typography>
                        <Button
                            variant='outlined'
                            color='inherit'
                            onClick={this.onSelectButtonClick}
                            style={{marginLeft: 'auto'}}
                            aria-label={'Select ' + preset.name}
                        >
                            Select
                        </Button>
                    </CardActions>
                    <VideoViewerDialog
                        src={preset.exampleUrl}
                        open={viewerOpen}
                        onClose={() => this.setState({viewerOpen: false})}
                        iframe
                    />
                    <ConfirmationDialog
                        title={'Apply ' + preset.name + '?'}
                        open={confirmationOpen}
                        onClose={() => this.setState({confirmationOpen: false})}
                        confirmation={this.onPresetApplyConfirm}
                        showCheck
                        storageKey='SKIP_PRESET_CONFIRMATION'
                    >
                        This will overwrite your current settings with the preset settings. Your images, text, and audio will remain the same.
                    </ConfirmationDialog>
                </Card>
            </PresetCardBadge>
        );
    }

    onCardAreaClick = () => {
        const { preset } = this.props;
        this.setState({viewerOpen: true});

        trackEvent('Viewed Preset Video', {
            name: preset.name,
            category: AnalyticsConstants.VIDEO_CATEGORY,
        });
    }

    onPresetApplyConfirm = () => {
        this.applyVideoPreset();
    }

    onSelectButtonClick = () => {
        const { videoSettingsId, videoDirty, savingInProgress, showCriticalOperationProgressWarning } = this.props;

        if (savingInProgress) {
            showCriticalOperationProgressWarning();
            return;
        }

        if (!videoSettingsId && !videoDirty) {
            this.applyVideoPreset();
            return;                                
        }

        this.setState({confirmationOpen: true});
    }

    applyVideoPreset = () => {
        const { applyPreset, preset, openPremiumVideosDialog, subscriptionType, setProperty } = this.props;

        if (subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE && preset.isPremium) {
            setProperty('presetDialogOpen', false);
            openPremiumVideosDialog(preset.exampleUrl, () => {
                setProperty('presetDialogOpen', true);
            }, 'presets');
        } else {
            applyPreset(preset);
        }
    }
}

const mapStateToProps = (state, ownProps) => {
    // Bind the component to an item from list in order to render it separately
    const preset = state.create.presets[ownProps.index];

    const { videoDirty } = state.create;
    const { id: videoSettingsId } = state.createPreview.present.videoSettings;
    const { subscriptionType } = state.app.accountDetails;
    const { savingInProgress } = state.create.videoSavingState;

    return {
        preset,
        videoDirty,
        videoSettingsId,
        subscriptionType,
        savingInProgress
    }
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const applyPreset = (preset) => dispatch(createActions.applyPreset(preset));
    const openPremiumVideosDialog = (videoUrl, onGoBack, location) => dispatch( mediaPickerActions.openPremiumVideosDialog(videoUrl, onGoBack, location));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());

    return {
        setProperty,
        applyPreset,
        openPremiumVideosDialog,
        showCriticalOperationProgressWarning
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(PresetCard, styles));